import React, {useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef} from 'react';
import {Tab, Tabs} from 'react-bootstrap';
import ZoomIndicator from './components/ZoomIndicator';
import useDjpalTokenRenewal from './hooks/useDjpalTokenRenewal';
import LoginModal from './components/Auth/LoginModal';
import RegisterModal from './components/Auth/RegisterModal';
import ForgotPasswordModal from './components/Auth/ForgotPasswordModal';
import ReportProblemModal from './components/ReportProblem/ReportProblemModal';
import UserMenu from './components/Auth/UserMenu';
import AboutTab from './components/Tabs/AboutTab';
import SubscriptionTab from './components/Tabs/SubscriptionTab';
import AccountTab from './components/Tabs/AccountTab';
import SettingsTab from './components/Tabs/SettingsTab';
import NotificationsTab from './components/Tabs/NotificationsTab';
import BackupTab from './components/Tabs/BackupTab';
import NavbarSearch from './components/NavbarSearch';
import FindOverlay from './components/FindOverlay';
import NavButtons from './components/NavButtons';
import MatchLoudnessTab from './components/Tabs/MatchLoudnessTab';
import OrganizeTracksTab from './components/Tabs/OrganizeTracksTab';
import VisualizeLibraryTab from './components/Tabs/VisualizeLibraryTab';
import FixMetadataTab from './components/Tabs/FixMetadataTab';
import MigrateLibraryTab from './components/Tabs/MigrateLibraryTab';
import FixMyTracksTab from './components/Tabs/FixMyTracksTab';
import CuePointsTab from './components/Tabs/CuePointsTab';
import AutomixTab from './components/Tabs/AutomixTab';
import AutomixBuildWatcher from './components/Automix/AutomixBuildWatcher';
import DJPalTab from './components/Tabs/DJPalTab';
import SmartRecsTab from './components/Tabs/SmartRecsTab';
import WhatsNextTab from './components/Tabs/WhatsNextTab';
import FeedTab from './components/Tabs/FeedTab';
// import AISetTab from './components/Tabs/AISetTab'; // Disabled for now
import CategoryView from './components/CategoryView';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/App.scss';
import MixGoatLogo from "./components/MixGoatLogo";
import {useSelector, useDispatch, useStore} from "react-redux";
import {setActiveTab, setActiveCategory} from "./redux/slices/appSlice";
import {openReportModal} from "./redux/slices/reportSlice";
import {setLikesSidebarOpen} from "./redux/slices/likesSlice";
import {setFollowedArtistsSidebarOpen} from "./redux/slices/followedArtistsSlice";
import {setSidebarOpen as setPlaylistSidebarOpen} from "./redux/slices/playlistSlice";
import {checkAuthStatus, refreshAccessToken} from './redux/thunk/authThunk';
import {setIsLoading, setUser} from './redux/slices/authSlice';
import {setActiveSearch, setArtistQuery, setTrackQuery, setMixQuery, setSearchAllQuery, setMixFilterContext, setTrackListQuery, restoreDjpalSnapshot, DJPAL_SNAPSHOT_KEYS} from './redux/slices/djpalSlice';
import {publicEntityId} from './utils/entityId';
import {fromSlug, mixRouteToSearch, artistRouteToSearch, trackRouteToSearch, mixFilterFiltersFromUrl, eventPageFromUrl} from './utils/mixpediaRoutes';
import {searchArtist, searchTrack, searchMixes, searchTracksList} from './redux/thunk/djpalThunk';
import {navigateHistory} from './redux/thunk/appNavThunk';
import {loadSettings, loadPreferences} from './redux/thunk/settingsThunk';
import {resolveTheme, applyTheme} from './utils/themeResolver';
import {readDesignV2, applyDesignV2, useDesignV2} from './utils/designV2';
import {updateSettings} from './redux/slices/settingsSlice';
import './styles/_theme.scss';
import {loadCuePointsSettings} from './redux/thunk/cuePointsSettingsThunk';
import {loadAutomixEngine} from './redux/thunk/automixThunk';
import {loadTabSettings} from './redux/thunk/tabSettingsThunk';
import MusicPlayer from "./components/MusicPlayer";
import NativeMusicPlayer from "./NativeMusicPlayer";
import NativeLockScreenRefill from "./components/NativeLockScreenRefill";
import NativeLockScreenLike from "./components/NativeLockScreenLike";
import NativeSamplePrewarm from "./components/NativeSamplePrewarm";
import MobilePaywall from "./components/MobilePaywall";
import PlaylistSidebar from "./components/PlaylistSidebar";
import LikedTracksSidebar from "./components/LikedTracksSidebar";
import FollowedArtistsSidebar from "./components/FollowedArtistsSidebar";
import RekordboxSidebar from "./components/RekordboxSidebar";
import SeratoSidebar from "./components/SeratoSidebar";
import TraktorSidebar from "./components/TraktorSidebar";
import VirtualDJSidebar from "./components/VirtualDJSidebar";
import {ToastProvider} from "./contexts/ToastContext";
import ConfirmationToastBridge from "./components/ConfirmationToastBridge";
import DesignLab from "./components/DesignLab";
import DjLibraryChangeListener from "./components/DjLibraryChangeListener";
import AutoSyncManager from "./components/AutoSyncManager";
import DjSyncStateHydrator from "./components/DjSyncStateHydrator";
import RestoreFoundPrompt from "./components/Backup/RestoreFoundPrompt";
import BackupOfferPrompt from "./components/Backup/BackupOfferPrompt";
import ShrinkGuardPrompt from "./components/Backup/ShrinkGuardPrompt";
import BackupProgressToast from "./components/BackupProgressToast";
import {GoatUpgradeModal, TrialBanner} from './components/Tier';
import {initializeTier, refreshTier} from './redux/thunk/tierThunk';
import {showUpgradeModal} from './redux/slices/tierSlice';
import {MIXPEDIA_FREE_FOR_ALL} from '../backend/planModel';
import {fetchFeed, hydrateWebFeedFromCache} from './redux/thunk/feedThunk';
import {fetchUnreadCount, fetchNotifications} from './redux/thunk/notificationsThunk';
import {fetchFollowedArtists} from './redux/thunk/followedArtistsThunk';
import { fetchFollowedLabels } from './redux/thunk/followedLabelsThunk';
import {prefetchHomeRows, hydrateHomeRowsSnapshot, homeRowsPopulated} from './components/DJPal/mixpediaHomeData';
import {syncAppBadge, clearDeliveredNotifications} from './utils/appBadge';
import NotificationBell from './components/NotificationBell';
import NotificationPanel from './components/NotificationPanel';
import WebPlaybackCTA from './components/WebPlaybackCTA';
import OnboardingFlow from './components/Onboarding/OnboardingFlow';
import ForceUpgradeScreen from './components/ForceUpgrade/ForceUpgradeScreen';
import UpdateNudgeBanner from './components/ForceUpgrade/UpdateNudgeBanner';
import MobileBottomNav from './components/MobileBottomNav';
import LibraryTab from './components/Tabs/LibraryTab';
import NowPlayingPage from './components/NowPlayingPage';

import { isWeb, isNative, isIOS } from './utils/webOnly';
import { captureOpenedWebUrl, takeOpenedWebUrl } from './utils/webDeepLink';
import { shouldPresentTrialEndSheet, readTrialEndSeen, writeTrialEndSeen } from './utils/trialEndSheet';
import { TRIAL_ENDED_FEATURE } from './utils/paywall';
import MixpediaHint from './components/DJPal/MixpediaHint';
import { getSeenHints, markHintSeen, getShownHints, markHintShown } from './utils/mixpediaHints';
import { track } from './utils/analytics';
import { isEmptySrcMediaError } from './utils/isEmptySrcMediaError';
import { ReactComponent as mixpediaIcon } from './assets/icons/categories/mixpedia.svg';
import { ReactComponent as recommendationsIcon } from './assets/icons/categories/recommendations.svg';
import { ReactComponent as libraryIcon } from './assets/icons/categories/library.svg';
import { ReactComponent as organizeIcon } from './assets/icons/categories/organize.svg';
import { ReactComponent as metadataIcon } from './assets/icons/categories/metadata.svg';
import { ReactComponent as migrateIcon } from './assets/icons/categories/migrate.svg';
import { ReactComponent as compatibilityIcon } from './assets/icons/categories/compatibility.svg';
import { ReactComponent as loudnessIcon } from './assets/icons/categories/loudness.svg';
import { ReactComponent as cuePointsIcon } from './assets/icons/categories/cuepoints.svg';
import { ReactComponent as automixIcon } from './assets/icons/categories/automix.svg';
import { ReactComponent as whatsnextIcon } from './assets/icons/categories/whatsnext.svg';
import { ReactComponent as feedIcon } from './assets/icons/categories/feed.svg';
import { ReactComponent as aisetIcon } from './assets/icons/categories/aiset.svg';
import goatMark from '../shared/goatMark';


// A/B flag for the "larger web UI" experiment (bigger navbar, logo, search,
// sidebar icons, feed text). Flip to false to instantly A/B against baseline.
// When true, adds `web-larger-ui` class to <html> and web-scoped CSS kicks in.
const WEB_LARGER_UI = false;
if (typeof document !== 'undefined' && isWeb) {
    document.documentElement.classList.toggle('web-larger-ui', WEB_LARGER_UI);
}

// Flag: hover-expand on the left rail (Instagram-style slide-out with labels).
// Disabled for now — rail is icons-only. Flip to `true` to restore the behavior.
// When true, adds `rail-hover-expand` class to <html> and the hover CSS kicks in.
const RAIL_HOVER_EXPAND = false;
if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle('rail-hover-expand', RAIL_HOVER_EXPAND);
}

// Platform-specific html class. Android-scoped CSS can target `html.platform-android`
// to fix Android-only rendering quirks without touching iOS / web / desktop.
if (typeof document !== 'undefined' && typeof window !== 'undefined' && window.Capacitor?.getPlatform) {
    const _plat = window.Capacitor.getPlatform();
    document.documentElement.classList.toggle('platform-android', _plat === 'android');
    document.documentElement.classList.toggle('platform-ios', _plat === 'ios');
}

// Windows Electron desktop only: `html.platform-win` lets CSS fix Windows-only
// rendering issues (e.g. the hub tiles crushing on the short logical viewports
// typical of 1080p @ 150% DPI) with ZERO effect on macOS / web / mobile.
// Real desktop = window.electron.db exists (web + Capacitor shims deliberately
// omit `db` — see utils/webOnly.js); Windows = navigator.platform 'Win32'.
if (typeof document !== 'undefined' && typeof window !== 'undefined') {
    const _isWinDesktop = !!(window.electron && window.electron.db)
        && typeof navigator !== 'undefined' && /^Win/.test(navigator.platform || '');
    document.documentElement.classList.toggle('platform-win', _isWinDesktop);
}

// Category definitions
const ALL_CATEGORIES = [
    {
        id: 'explore',
        label: 'Discover',
        subtitle: 'Discover the Scene',
        features: [
            {key: 'feed', title: 'For You', desc: 'Personalized feed based on your likes and artists', icon: feedIcon, component: FeedTab},
            {key: 'djpal', title: 'Mixpedia', desc: 'See what DJs play in their sets', icon: mixpediaIcon, component: DJPalTab},
            {key: 'whats-next', title: 'Set Planner', desc: 'Plan your set visually, track by track', icon: whatsnextIcon, component: WhatsNextTab},
            {key: 'smart-recs', title: 'Crate Digger', desc: 'Find what DJs play with your tracks', icon: recommendationsIcon, component: SmartRecsTab},
            // {key: 'ai-set', title: 'AI Set', desc: 'Auto-generate a DJ set with transitions', icon: aisetIcon, component: AISetTab},
        ]
    },
    {
        id: 'library',
        label: 'Manage',
        subtitle: 'Organize and Maintain Your Library',
        features: [
            {key: 'visualize', title: 'Library', desc: 'Browse and analyze your tracks', icon: libraryIcon, component: VisualizeLibraryTab},
            {key: 'organize-tracks', title: 'Organize', desc: 'Clean filenames and folder structure', icon: organizeIcon, component: OrganizeTracksTab},
            {key: 'fix-metadata', title: 'Metadata', desc: 'Edit and enrich track tags', icon: metadataIcon, component: FixMetadataTab},
            {key: 'migrate-library', title: 'Migrate', desc: 'Convert between DJ software', icon: migrateIcon, component: MigrateLibraryTab},
        ]
    },
    {
        id: 'prepare',
        label: 'Prepare',
        subtitle: 'Get Set Ready',
        features: [
            {key: 'fix-my-tracks', title: 'Compatibility', desc: 'Fix format issues for your DJ software', icon: compatibilityIcon, component: FixMyTracksTab},
            {key: 'match-loudness', title: 'Loudness', desc: 'Match volume levels across tracks', icon: loudnessIcon, component: MatchLoudnessTab},
            {key: 'cue-points', title: 'Cues & Loops', desc: 'Auto-detect cues & loops and prep your tracks', icon: cuePointsIcon, component: CuePointsTab},
            // Shown only where the mixing engine can run (CategoryView reads automix.engine).
            {key: 'automix', title: 'Automix', desc: 'Turn a playlist into a mixed DJ set', icon: automixIcon, component: AutomixTab},
        ]
    },
];

// On web and native, only show Discover category (file/migration features are desktop-only)
const CATEGORIES = (isWeb || isNative) ? ALL_CATEGORIES.filter(c => c.id === 'explore') : ALL_CATEGORIES;

// Record the URL the web app was opened at before any effect can rewrite the
// address bar; the deep-link parser in App applies it on the first signed-in
// pass, so a signed-out visitor still lands where the link pointed.
if (isWeb) captureOpenedWebUrl();

// Show/hide the pure-HTML loading overlay that lives outside React in index.html.
// Because it's not a React component, heavy React renders can't freeze its animation.
function showLoadingOverlay() {
    const el = document.getElementById('app-loading-overlay');
    if (el) {
        // DESIGN V2: the logo as a loading screen (goat, "Mix" + accent "Goat",
        // slim bar; styled in _chrome.scss), so a launch reads as one loading screen.
        if (document.documentElement.getAttribute('data-design') === 'v2' && !el.querySelector('.v2-boot-mark')) {
            el.querySelector('.wordmark')?.insertAdjacentHTML('afterbegin',
                `<svg class="v2-boot-mark" viewBox="${goatMark.GOAT_MARK_VIEWBOX}" aria-hidden="true"><path fill-rule="evenodd" d="${goatMark.GOAT_MARK_PATH}"/></svg>`);
            el.insertAdjacentHTML('beforeend', '<div class="v2-boot-bar"><i></i></div>');
        }
        el.classList.remove('fade-out');
        el.style.display = '';
    }
}
function hideLoadingOverlay() {
    const el = document.getElementById('app-loading-overlay');
    if (el) {
        el.classList.add('fade-out');
        setTimeout(() => { if (el) el.style.display = 'none'; }, 200);
    }
}


function App() {
    const activeTab = useSelector((state) => state.app.activeTab)
    const activeCategory = useSelector((state) => state.app.activeCategory)
    // Use individual selectors to prevent re-renders when unrelated playlist state changes
    const sidebarOpen = useSelector((state) => state.playlist.sidebarOpen);
    const sidebarWidth = useSelector((state) => state.playlist.sidebarWidth);
    const rekordboxSidebarOpen = useSelector((state) => !isWeb && state.rekordbox.sidebarOpen);
    const rekordboxSidebarWidth = useSelector((state) => state.rekordbox.sidebarWidth);
    const seratoSidebarOpen = useSelector((state) => !isWeb && state.serato.sidebarOpen);
    const seratoSidebarWidth = useSelector((state) => state.serato.sidebarWidth);
    const traktorSidebarOpen = useSelector((state) => !isWeb && state.traktor.sidebarOpen);
    const traktorSidebarWidth = useSelector((state) => state.traktor.sidebarWidth);
    const virtualdjSidebarOpen = useSelector((state) => !isWeb && state.virtualdj.sidebarOpen);
    const virtualdjSidebarWidth = useSelector((state) => state.virtualdj.sidebarWidth);
    const likesSidebarOpen = useSelector((state) => state.likes.sidebarOpen);
    const likesSidebarWidth = useSelector((state) => state.likes.sidebarWidth);
    const followedArtistsSidebarOpen = useSelector((state) => state.followedArtists.sidebarOpen);
    const followedArtistsSidebarWidth = useSelector((state) => state.followedArtists.sidebarWidth);
    const playerMode = useSelector((state) => state.musicPlayer.mode);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const currentUserId = useSelector((state) => state.auth.user?.id);
    const tierLoaded = useSelector((state) => state.tier.isLoaded);
    const effectiveTier = useSelector((state) => state.tier.effectiveTier);
    const isTrialExpired = useSelector((state) => state.tier.isTrialExpired);
    const onboardingActive = useSelector((state) => state.app.onboardingActive);
    const onboardingDecided = useSelector((state) => state.app.onboardingDecided);
    const hasDesktopApp = useSelector((state) => !!state.auth.user?.last_desktop_login_at);
    const theme = useSelector((state) => state.settings.theme);
    const designV2 = useDesignV2();
    // The loading screen (main process) shows the v2 goat when v2 is on, in
    // the light or dark theme the app was last in.
    // A main process older than this renderer has no handler (dev reloads before an
    // Electron restart): the splash colour is cosmetic, so a miss is ignored.
    useEffect(() => { window.electron?.design?.setV2?.(designV2 ? resolveTheme(theme) : null)?.catch?.(() => {}); }, [designV2, theme]);
    const notifUnreadCount = useSelector((state) => state.notifications.unreadCount);
    const dispatch = useDispatch();
    const store = useStore();

    // Session-long DJ Pal token renewal — must live at the app shell, not in
    // a tab: the default landing view mounts none of the Mixpedia tabs, and
    // the 30-day token has to renew even in sessions that never open one.
    useDjpalTokenRenewal();

    // Phased mount behind the HTML overlay spinner.
    // Each phase mounts a small chunk, with setTimeout gaps so the
    // spinner (outside React) keeps animating between commits.
    // 0=nothing, 1=sidebars, 2=tabs+content, 3=music player, 4=done
    const [mountPhase, setMountPhase] = useState(0);
    // Mobile layout must NOT depend on orientation: a phone in landscape still
    // wants the mobile UI. Native is always mobile; on web we treat a small
    // width OR a short height (landscape phone) as mobile, so rotating never
    // flips into the desktop layout. (Native is also orientation-locked to
    // portrait at the OS level — this is the web-side guard + belt-and-braces.)
    const isMobileWeb = isWeb && typeof window !== 'undefined' && (
        isNative
        || window.matchMedia('(max-width: 768px)').matches
        || window.matchMedia('(max-height: 480px)').matches
    );
    const [wasAuthenticated, setWasAuthenticated] = useState(false);

    // On web, default to Feed tab (no hub grid)
    useEffect(() => {
        if (isWeb && !activeTab) {
            dispatch(setActiveTab('feed'));
        }
    }, [activeTab, dispatch]);

    const handleSelectCategory = useCallback((k) => {
        if (!k) {
            dispatch(setActiveCategory('explore'));
            return;
        }
        window.electron.analytics.track('category_view', { category: k });
        dispatch(setActiveCategory(k));
    }, [dispatch]);

    const tabKey = activeCategory;

    // Build the category config for the currently active category
    const currentCategory = useMemo(() =>
        CATEGORIES.find(c => c.id === activeCategory),
        [activeCategory]
    );

    useEffect(() => {
        dispatch(checkAuthStatus());
        // Capacitor cold-start recovery: if iOS evicted WKWebView's
        // localStorage but Keychain still had the session, the shim
        // restores localStorage then fires this event. Re-run the auth
        // check so the user lands on the feed instead of the login modal.
        const onRehydrate = () => dispatch(checkAuthStatus());
        window.addEventListener('capacitor-auth-rehydrated', onRehydrate);
        return () => window.removeEventListener('capacitor-auth-rehydrated', onRehydrate);
    }, [dispatch]);

    // Refresh profile from DB and update tier — used by deep link and tab focus
    const refreshProfile = useCallback(async () => {
        // Electron stores token under 'auth:accessToken'; Capacitor shim uses 'web-shim:dev-access-token'.
        const token = localStorage.getItem('auth:accessToken') || localStorage.getItem('web-shim:dev-access-token');
        if (!token) return;
        const profile = await window.electron.auth.getProfile(token);
        if (profile && !profile.error && profile.email) {
            dispatch(setUser(profile));
            dispatch(refreshTier());
        } else {
            await dispatch(refreshAccessToken());
            const newToken = localStorage.getItem('auth:accessToken') || localStorage.getItem('web-shim:dev-access-token');
            const retryProfile = await window.electron.auth.getProfile(newToken);
            if (retryProfile && !retryProfile.error) {
                dispatch(setUser(retryProfile));
                dispatch(refreshTier());
            }
        }
    }, [dispatch]);

    // Listen for payment-success deep link from Paddle checkout (desktop)
    useEffect(() => {
        if (!window.electron.onPaymentSuccess) return;
        return window.electron.onPaymentSuccess(refreshProfile);
    }, [refreshProfile]);

    // Refresh user profile + tier on refocus, but DEFERRED via
    // requestIdleCallback so we never block the refocus paint.
    //
    // Background: the original synchronous refocus refresh fired
    // window.electron.auth.getProfile() immediately on focus. That IPC
    // round-trips to auth.mixgoat.com (~3-3.5s) and the dispatch cascade
    // when it returns (setUser → refreshTier → another auth.getProfile →
    // setUser → tier.getInfo → setTierInfo) caused the user-reported
    // "freeze for 2-3 seconds after coming back". Diagnostic capture
    // confirmed: only one dispatch in the 5s window after focus, exactly
    // 3.45s after the focus event, which is the IPC RTT. Disabling the
    // refresh entirely made the freeze disappear.
    //
    // Wrapping in requestIdleCallback gives us back the tier-staleness
    // coverage without blocking — the IPC fires only when the renderer
    // is idle (typically a few seconds after refocus, no UI work in
    // flight). If the user takes a tier-gated action before the refresh
    // resolves, the action's own auth check will 401 and re-auth.
    //
    // Cooldown stays at 60s to suppress duplicate refresh on the burst
    // of focus + visibilitychange events that fire on a single Cmd-Tab.
    useEffect(() => {
        if (!isAuthenticated) return;
        let lastRefocusAt = 0;
        const REFOCUS_COOLDOWN_MS = 60 * 1000;
        const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1000));
        const onRefocus = () => {
            if (document.visibilityState !== 'visible') return;
            const now = Date.now();
            if (now - lastRefocusAt < REFOCUS_COOLDOWN_MS) return;
            lastRefocusAt = now;
            idle(() => refreshProfile(), { timeout: 5000 });
        };
        window.addEventListener('focus', onRefocus);
        document.addEventListener('visibilitychange', onRefocus);

        return () => {
            window.removeEventListener('focus', onRefocus);
            document.removeEventListener('visibilitychange', onRefocus);
        };
    }, [isAuthenticated, refreshProfile]);

    // Seed initial nav history entry so back works from the first interaction.
    // Desktop AND native (both drive the app-level nav stack); real web uses
    // browser history instead.
    useEffect(() => {
        if (isWeb && !isNative) return;
        dispatch(setActiveCategory('explore'));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // App-level back/forward: Magic Mouse / trackpad swipes, mouse side buttons, Cmd+[/]
    // Desktop only — web uses native browser back/forward via URL history.
    useEffect(() => {
        if (isWeb) return;

        let accDeltaX = 0;
        let swipeTimeout = null;
        let fired = false;

        const handleWheel = (e) => {
            if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
            // Don't hijack the swipe as back/forward when it happens over a
            // horizontally-scrollable element (e.g. the library tracks table) —
            // let that element scroll instead. overscroll-behavior only stops the
            // native gesture, not this window-level wheel handler.
            for (let el = e.target; el && el !== document.body; el = el.parentElement) {
                if (el.scrollWidth > el.clientWidth + 1) {
                    const ox = getComputedStyle(el).overflowX;
                    if (ox === 'auto' || ox === 'scroll') return;
                }
            }
            accDeltaX += e.deltaX;
            if (!fired && Math.abs(accDeltaX) > 80) {
                fired = true;
                dispatch(navigateHistory(accDeltaX > 0 ? 'forward' : 'back'));
            }
            clearTimeout(swipeTimeout);
            swipeTimeout = setTimeout(() => { accDeltaX = 0; fired = false; }, 200);
        };

        const handleMouseNav = (e) => {
            if (e.button === 3) { e.preventDefault(); dispatch(navigateHistory('back')); }
            else if (e.button === 4) { e.preventDefault(); dispatch(navigateHistory('forward')); }
        };

        const handleKeyNav = (e) => {
            if (!e.metaKey && !e.ctrlKey) return;
            if (e.key === '[') { e.preventDefault(); dispatch(navigateHistory('back')); }
            else if (e.key === ']') { e.preventDefault(); dispatch(navigateHistory('forward')); }
        };

        window.addEventListener('wheel', handleWheel, { passive: true });
        window.addEventListener('mousedown', handleMouseNav);
        window.addEventListener('keydown', handleKeyNav);

        const cleanupIpc = window.electron?.djpal?.onNav?.((direction) => {
            dispatch(navigateHistory(direction === 'forward' ? 'forward' : 'back'));
        });

        return () => {
            window.removeEventListener('wheel', handleWheel);
            window.removeEventListener('mousedown', handleMouseNav);
            window.removeEventListener('keydown', handleKeyNav);
            clearTimeout(swipeTimeout);
            cleanupIpc?.();
        };
    }, [dispatch]);

    // Navigate with a page-slide: snapshot the current screen into a fixed
    // overlay and slide it off (back → right, forward → left), revealing the
    // new screen React renders underneath. Clone is captured synchronously
    // BEFORE the state change so timing is robust. Returns whether it moved.
    // withinTab: the iOS gesture pops the current tab's stack only — it never
    // switches tabs (the bottom bar is the tab switcher).
    const navWithSlide = useCallback((dir) => {
        let clone = null;
        const host = document.querySelector('.tab-content');
        if (host) {
            const r = host.getBoundingClientRect();
            clone = host.cloneNode(true);
            clone.classList.add('nav-slide-clone');
            Object.assign(clone.style, {
                position: 'fixed', left: r.left + 'px', top: r.top + 'px',
                width: r.width + 'px', height: r.height + 'px', margin: '0',
                zIndex: '2147483000', pointerEvents: 'none', overflow: 'hidden',
                background: 'var(--color-bg-primary, #0a0a0a)',
            });
            document.body.appendChild(clone);
        }
        const moved = dispatch(navigateHistory(dir, { withinTab: true }));
        if (!moved) { if (clone) clone.remove(); return false; }
        if (clone) {
            const DUR = 260;
            requestAnimationFrame(() => {
                clone.style.animation = (dir === 'back' ? 'navPageOutRight' : 'navPageOutLeft') + ' ' + DUR + 'ms ease forwards';
            });
            setTimeout(() => { try { clone.remove(); } catch { /* noop */ } }, DUR + 80);
        }
        return true;
    }, [dispatch]);

    // iOS edge-swipe nav: left edge → back, right edge → forward (Safari's
    // two-edge model), both withinTab so a gesture never lands you in a
    // different tab. v2 lessons baked in: EDGE-START ONLY is what makes this
    // safe — v1 misfired because it accepted full-surface starts, not because
    // of when it committed. So v3 commits mid-move the moment the threshold
    // is crossed (instant response) with touchend as the slow-swipe fallback.
    // Passive listeners → never blocks vertical scrolling. Android has NO JS
    // swipe-nav by design — its system Back drives the backButton handler.
    useEffect(() => {
        if (!isIOS) return undefined;
        const EDGE = 44;   // px from either screen edge where a nav-swipe may begin
        const THRESH = 60; // px horizontal travel to commit
        // Only genuine interactive horizontal controls — NOT broad class globs
        // (those matched feed ancestors and killed every swipe). Any new
        // horizontally-draggable surface MUST carry role="slider" or
        // data-swipe-ignore, or this handler will claim its drags.
        const IGNORE_SEL = 'input, textarea, [role="slider"], .rc-slider, [data-swipe-ignore]';
        let start = null;

        // Nearest ancestor that is an actually-scrollable horizontal rail
        // (overflow-x auto/scroll + wider content). A swipe that starts on a
        // rail belongs to the rail — critical for the right-edge forward
        // gesture, which is otherwise identical to dragging a carousel left.
        // No width heuristic: full-width phone rails span ~90%+ of the
        // viewport; overflow-x is the real discriminator.
        const hScrollerOf = (el) => {
            for (let n = el; n && n !== document.body; n = n.parentElement) {
                if (n.scrollWidth > n.clientWidth + 8) {
                    const ox = getComputedStyle(n).overflowX;
                    if (ox === 'auto' || ox === 'scroll') return n;
                }
            }
            return null;
        };

        const onStart = (e) => {
            if (!e.touches || e.touches.length !== 1) { start = null; return; }
            const t = e.touches[0];
            const w = window.innerWidth;
            const from = t.clientX <= EDGE ? 'left' : (t.clientX >= w - EDGE ? 'right' : null);
            if (!from) { start = null; return; }
            // Open dialogs/sheets own their surface (same guard as the
            // Android backButton handler).
            if (document.querySelector('.auth-overlay, [role="dialog"]')) { start = null; return; }
            if (e.target?.closest?.(IGNORE_SEL) || hScrollerOf(e.target)) { start = null; return; }
            start = { x: t.clientX, y: t.clientY, from };
        };
        const tryCommit = (t) => {
            if (!start) return;
            const dx = t.clientX - start.x;
            const dy = Math.abs(t.clientY - start.y);
            // Travel INWARD from the starting edge: left edge drags right
            // (back), right edge drags left (forward).
            const travel = start.from === 'left' ? dx : -dx;
            if (travel < THRESH || travel < dy * 1.2) return;
            const dir = start.from === 'left' ? 'back' : 'forward';
            start = null;
            navWithSlide(dir);
        };
        const onMove = (e) => { if (start && e.touches?.length === 1) tryCommit(e.touches[0]); };
        const onEnd = (e) => { if (start && e.changedTouches?.length) tryCommit(e.changedTouches[0]); start = null; };
        const onCancel = () => { start = null; };
        document.addEventListener('touchstart', onStart, { passive: true });
        document.addEventListener('touchmove', onMove, { passive: true });
        document.addEventListener('touchend', onEnd, { passive: true });
        document.addEventListener('touchcancel', onCancel, { passive: true });
        return () => {
            document.removeEventListener('touchstart', onStart);
            document.removeEventListener('touchmove', onMove);
            document.removeEventListener('touchend', onEnd);
            document.removeEventListener('touchcancel', onCancel);
        };
    }, [navWithSlide]);

    // Android: drive BACK from the system back gesture / button (Capacitor
    // emits `backButton`). The edge swipe-from-left is owned by Android's
    // gesture-nav, so we consume that event for in-app back instead of letting
    // it exit the app — as long as there's history to pop. iOS has no such
    // event; it uses the edge-swipe handler above. Unlike the iOS gesture,
    // this back is NOT withinTab — system back conventionally walks the whole
    // app history.
    useEffect(() => {
        if (!isNative) return undefined;
        const App = window.Capacitor?.Plugins?.App;
        if (!App?.addListener) return undefined;
        let handle = null;
        const sub = App.addListener('backButton', () => {
            // Open modals/overlays handle their own back (they register their
            // own listener); don't navigate underneath them.
            if (document.querySelector('.auth-overlay, [role="dialog"]')) return;
            // Plain (instant) in-app back — the system gesture already provides
            // its own motion; layering our slide on top was the "weird" back.
            // Don't minimize/exit when there's no history — just stay put.
            dispatch(navigateHistory('back'));
        });
        Promise.resolve(sub).then((h) => { handle = h; });
        return () => { try { handle?.remove?.(); } catch { /* noop */ } };
    }, [dispatch]);

    // Suppress audio/video loading errors from showing in React error overlay
    useEffect(() => {
        const handleError = (event) => {
            const target = event.target || event.srcElement;
            if (target && (target.tagName === 'AUDIO' || target.tagName === 'VIDEO')) {
                // preventDefault() suppresses the React/dev error overlay.
                // Do NOT call stopPropagation(): this listener runs in the
                // CAPTURE phase on window, so stopping propagation here kills
                // the event before it descends to the <audio> element — where
                // react-jinke-music-player binds its own `error` listener
                // (onAudioError). That handler owns the entire recovery chain
                // (reset row UI via djpal-track-stop-all, list-context advance,
                // sample fallback, toast). Severing it left a failed track
                // showing "playing" in the feed with nothing in the player.
                event.preventDefault();
                // Benign "no track loaded yet" empty-src state — stay silent.
                if (!isEmptySrcMediaError(target)) {
                    console.warn('[App] Suppressed media loading error');
                }
                return false;
            }
        };

        window.addEventListener('error', handleError, true);
        return () => window.removeEventListener('error', handleError, true);
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(initializeTier());
        }
    }, [isAuthenticated, dispatch]);

    // Native (iOS/Android): the trial-end sheet — the ONE auto-presented
    // paywall. Every new account gets a card-free 14-day GOAT trial; the first
    // time this account is seen on Free with that trial expired, present the
    // paywall once as `trial_ended` (closable from the first frame), then never
    // auto-present again. Every other opening is the user's own tap on a free
    // wall. Accounts with no server trial get nothing here. Decision logic +
    // the per-account "seen" flag live in utils/trialEndSheet.js; only while
    // the plan model gates Mixpedia / For You (src/backend/planModel.js).
    useEffect(() => {
        const present = shouldPresentTrialEndSheet({
            freeForAll: MIXPEDIA_FREE_FOR_ALL,
            isNative,
            isAuthenticated,
            tierLoaded,
            userId: currentUserId,
            effectiveTier,
            isTrialExpired,
            // decide() is async (awaits server prefs); without the 'decided'
            // gate a tier-resolve would present the sheet behind the
            // onboarding overlay and burn its once-only flag.
            onboardingDecided,
            onboardingActive,
            seen: readTrialEndSeen(currentUserId),
        });
        if (!present) return;
        writeTrialEndSeen(currentUserId);
        dispatch(showUpgradeModal(TRIAL_ENDED_FEATURE));
    }, [isAuthenticated, tierLoaded, effectiveTier, isTrialExpired, currentUserId, onboardingActive, onboardingDecided, dispatch]);

    // ── Native push notification registration (iOS / Android) ──
    // Runs ONLY on Capacitor native AND after the user is authenticated.
    // On web/Electron this is a hard no-op — window.electron.push only
    // exists when capacitor-shim.js initialized (isNative === true).
    //
    // Flow: requestPermission → register → getToken → POST /devices/register.
    // The server stores the (user_id, device_token) pair so the
    // notification fan-out can deliver an APNs/FCM push alongside the
    // existing in-app notification row. ZERO regression on existing
    // notification UX — push is purely additive.
    useEffect(() => {
        if (!isNative || !isAuthenticated) return;
        if (!window.electron || !window.electron.push) return;
        let cancelled = false;
        (async () => {
            try {
                const perm = await window.electron.push.requestPermission();
                if (cancelled || !perm || !perm.granted) return;
                const reg = await window.electron.push.register();
                if (cancelled || !reg || !reg.success) return;
                const token = await window.electron.push.getToken();
                if (cancelled || !token) return;
                const accessToken = localStorage.getItem('auth:accessToken')
                    || localStorage.getItem('web-shim:dev-access-token');
                if (!accessToken) return;
                // ios for the iOS Capacitor build, android for the Android one.
                // window.Capacitor.getPlatform() returns 'ios' | 'android' | 'web'.
                const platform = (window.Capacitor && window.Capacitor.getPlatform)
                    ? window.Capacitor.getPlatform()
                    : 'ios';
                await fetch('https://app.mixgoat.com/api/library/devices/register', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({ token, platform }),
                });
            } catch (err) {
                console.warn('[App] push registration failed', err);
            }
        })();
        return () => { cancelled = true; };
    }, [isAuthenticated]);

    // ── App-icon badge sync ──
    // Keep the native app-icon badge in lockstep with the unread count (the
    // single source of truth in Redux, maintained by NotificationBell + the
    // read/clear thunks). When it hits 0 we also clear delivered tray
    // notifications — that's what removes the Android launcher "dot" once the
    // user has read everything. Both helpers are hard no-ops on desktop/web.
    useEffect(() => {
        syncAppBadge(notifUnreadCount);
        if (!notifUnreadCount) clearDeliveredNotifications();
    }, [notifUnreadCount]);

    // Native-only: refresh the unread count promptly on app foreground and when
    // a push lands while foregrounded, so the badge reflects reality without
    // waiting for NotificationBell's 60s poll. The badge itself updates via the
    // effect above once the count lands.
    useEffect(() => {
        if (!isNative || !isAuthenticated) return;
        dispatch(fetchUnreadCount());
        // A push that lands (or is tapped) while the app is open must refresh
        // the in-app list too — not just the count — so the bell shows it live
        // without the user having to close + reopen the panel. The list is
        // otherwise only fetched on panel-open.
        const onPush = () => {
            dispatch(fetchUnreadCount());
            dispatch(fetchNotifications());
        };
        window.addEventListener('mixgoat:push-received', onPush);
        window.addEventListener('mixgoat:push-action', onPush);
        let appListener;
        (async () => {
            try {
                const CapApp = window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App;
                if (CapApp && CapApp.addListener) {
                    appListener = await CapApp.addListener('appStateChange', ({ isActive }) => {
                        if (isActive) dispatch(fetchUnreadCount());
                    });
                }
            } catch (_) { /* no-op */ }
        })();
        return () => {
            window.removeEventListener('mixgoat:push-received', onPush);
            window.removeEventListener('mixgoat:push-action', onPush);
            try { appListener && appListener.remove(); } catch (_) {}
        };
    }, [isAuthenticated, dispatch]);

    // Native-only: identify the authenticated MixGoat user to RevenueCat so
    // the App Store / Play Billing entitlement is credited to the right
    // account. The server webhook reconciles RevenueCat's appUserID back to
    // tier:reconciled — without logIn, purchases would be anonymous and the
    // server couldn't unlock GOAT for this user.
    useEffect(() => {
        if (!isNative || !isAuthenticated || !currentUserId) return;
        if (!window.electron || !window.electron.iap) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await window.electron.iap.logIn(currentUserId);
                if (!cancelled && res && res.error) {
                    console.warn('[App] RevenueCat logIn failed', res.error);
                }
            } catch (err) {
                if (!cancelled) console.warn('[App] RevenueCat logIn threw', err);
            }
        })();
        return () => { cancelled = true; };
    }, [isAuthenticated, currentUserId]);


    // Desktop-only: prefetch the feed once auth is ready so the first Feed-tab
    // render uses Redux cache instead of waiting ~seconds for the server round-trip.
    // Web has its own flow (see feedThunk + /feed/aipicks) and renders the feed
    // as the landing page, so a prefetch here wouldn't help there.
    useEffect(() => {
        if (isWeb || !isAuthenticated) return;
        const { lastFetchedAt, isLoading } = store.getState().feed;
        const stale = !lastFetchedAt || (Date.now() - lastFetchedAt > 5 * 60 * 1000);
        if (stale && !isLoading) dispatch(fetchFeed());
    }, [isAuthenticated, dispatch, store]);

    // Web-only: returning users paint instantly from a localStorage snapshot
    // of the previous feed. The real fetchFeed fires in parallel (see FeedTab)
    // and replaces items when it lands. Same pattern for the Mixpedia home
    // rows (Preferences-backed on native, where localStorage can be evicted).
    useEffect(() => {
        if (!isWeb || !isAuthenticated) return;
        dispatch(hydrateWebFeedFromCache());
        hydrateHomeRowsSnapshot().catch(() => {});
    }, [isAuthenticated, dispatch]);

    // --- Web URL routing ---
    // Map tab keys to URL paths
    const tabToPath = useCallback((tab) => {
        const map = { feed: '/', djpal: '/mixpedia', 'whats-next': '/set-planner', 'smart-recs': '/crate-digger', library: '/library', likes: '/likes', 'followed-artists': '/favourite-artists', playlists: '/playlists' };
        return map[tab] || '/';
    }, []);

    const pathToTab = useCallback((path) => {
        if (path === '/' || path === '') return 'feed';
        if (path.startsWith('/mixpedia')) return 'djpal';
        if (path.startsWith('/set-planner')) return 'whats-next';
        if (path.startsWith('/crate-digger')) return 'smart-recs';
        if (path.startsWith('/library')) return 'library';
        if (path.startsWith('/likes')) return 'likes';
        if (path.startsWith('/favourite-artists')) return 'followed-artists';
        if (path.startsWith('/playlists')) return 'playlists';
        if (path.startsWith('/search')) return 'djpal';
        if (path.startsWith('/tracks')) return 'djpal';
        if (path.startsWith('/artist/') || path.startsWith('/track/') || path.startsWith('/mix/')) return 'djpal';
        return 'feed';
    }, []);

    // Session-scoped cache keyed by URL. Populated while navigating forward;
    // read on popstate so back/forward restores prior results without re-fetching.
    const urlSnapshotCacheRef = useRef(new Map());
    // Holds a deep link that arrived before auth finished, flushed once ready.
    const pendingDeepLinkRef = useRef(null);

    // One-time "you can change zoom & theme" hint anchored to the profile
    // menu (desktop only — zoom/theme live in the desktop Account panel).
    // Users land on an adaptive zoom default, so tell them the display is
    // adjustable. Reuses the Mixpedia hint mechanics: auto-shows once (6s
    // after the navbar settles), beacon-only on later launches, gone forever
    // after "Got it".
    const appearanceHintRef = useRef(null);
    const [appearanceHint, setAppearanceHint] = useState(null); // null | {autoShow}
    useEffect(() => {
        if (isWeb || isNative) return undefined;
        if (!isAuthenticated || getSeenHints().includes('appearance_settings')) return undefined;
        const autoShow = !getShownHints().includes('appearance_settings');
        const t = setTimeout(() => {
            setAppearanceHint({ autoShow });
            if (autoShow) markHintShown('appearance_settings');
        }, 6000);
        return () => clearTimeout(t);
    }, [isAuthenticated]);

    // Warm the Mixpedia home rows at startup (same idea as the For You
    // prefetch) so the first visit shows content instead of skeletons. The
    // delay keeps it out of the cold-start critical path; fresh caches make
    // repeat calls no-ops. Runs on desktop AND Capacitor native (mobile was
    // the platform that most needed it — every request there also pays the
    // Cloudflare→auth-service proxy chain); browser web keeps fetch-on-visit
    // plus the snapshot hydrate above.
    //
    // It also RE-warms: the row cache has a 15-min TTL, so a one-shot startup
    // prefetch went stale in any app left open for a while, and the first
    // Mixpedia visit after that paid the full round-trip in skeletons ("the
    // app was open for ages, why isn't it loaded?"). A cheap periodic check
    // (prefetchHomeRows is a no-op while fresh) plus a focus/visibility hook
    // keeps it warm — but only while the window is visible, so a backgrounded
    // app doesn't poll the server all day. MixpediaHome additionally seeds
    // from the stale cache on mount, so even the gap between expiry and the
    // next tick paints content instead of skeletons.
    const followedLoadedForHome = useSelector((state) => state.followedArtists.loaded);
    useEffect(() => {
        if ((isWeb && !isNative) || !isAuthenticated) return undefined;
        if (!followedLoadedForHome) {
            dispatch(fetchFollowedArtists());
            dispatch(fetchFollowedLabels());
            return undefined;
        }
        const warm = () => {
            if (document.visibilityState === 'hidden') return;
            // No RE-warm while the user is inside Mixpedia: the home/Show-more
            // pages page through the cache by (seed, page), and a background
            // reroll under them would make the next "Show more" continue a
            // different roll. (The first warm of the session is always safe —
            // nothing to disturb.) Home refreshes itself on mount anyway.
            if (store.getState().app.activeTab === 'djpal' && homeRowsPopulated()) return;
            // Re-roll the tracks row's shuffle only while Mixpedia isn't on
            // screen — a warm under a visible (snapshot-seeded) home must
            // reproduce the same ordering or the rows visibly swap mid-read.
            const reroll = store.getState().app.activeTab !== 'djpal';
            prefetchHomeRows(dispatch, store.getState, { reroll }).catch(() => {});
        };
        const t = setTimeout(warm, 4000);
        const interval = setInterval(warm, 60 * 1000);
        document.addEventListener('visibilitychange', warm);
        window.addEventListener('focus', warm);
        return () => {
            clearTimeout(t);
            clearInterval(interval);
            document.removeEventListener('visibilitychange', warm);
            window.removeEventListener('focus', warm);
        };
    }, [isAuthenticated, followedLoadedForHome, dispatch, store]);

    // One-time hint for the desktop navbar search: explains that the header
    // searches Mixpedia (like the web) and that find-on-page moved to Cmd+F.
    const searchHintRef = useRef(null);
    const [searchHint, setSearchHint] = useState(null); // null | {autoShow}
    useEffect(() => {
        if (isWeb || isNative) return undefined;
        if (!isAuthenticated || getSeenHints().includes('desktop_search')) return undefined;
        const autoShow = !getShownHints().includes('desktop_search');
        const t = setTimeout(() => {
            setSearchHint({ autoShow });
            if (autoShow) markHintShown('desktop_search');
        }, 3000);
        return () => clearTimeout(t);
    }, [isAuthenticated]);

    // /library and its sub-routes (/likes, /favourite-artists, /playlists)
    // are the mobile-web pages backed by the Library hub. On desktop there
    // is no Library tab in the navbar, so visiting one of these URLs would
    // render a blank tab area. Redirect them to / and surface the
    // equivalent desktop UI (the slide-out sidebar) when applicable.
    const MOBILE_ONLY_PATHS = useMemo(() => new Set(['/library', '/likes', '/favourite-artists', '/playlists']), []);

    // Deep link handling: parse URL on initial load
    useEffect(() => {
        if (!isAuthenticated || !isWeb) return;
        // The first signed-in pass uses the URL the app was OPENED at: a
        // signed-out visitor's address bar has been reset to '/' by now
        // (see utils/webDeepLink.js). Later passes read the live URL.
        const opened = takeOpenedWebUrl();
        const path = opened ? opened.pathname : window.location.pathname;
        const search = opened ? opened.search : window.location.search;

        // Desktop redirect for mobile-only URLs: open the corresponding
        // slide-out sidebar (Likes/Favourites/Playlists) and rewrite the
        // URL to / so the desktop never sits on a route it can't render.
        if (!isMobileWeb && MOBILE_ONLY_PATHS.has(path)) {
            window.history.replaceState({ tab: 'feed' }, '', '/');
            dispatch(setActiveTab('feed'));
            if (path === '/likes') dispatch(setLikesSidebarOpen(true));
            else if (path === '/favourite-artists') dispatch(setFollowedArtistsSidebarOpen(true));
            else if (path === '/playlists') dispatch(setPlaylistSidebarOpen(true));
            // '/library' falls through with no sidebar open (no desktop equivalent)
            return;
        }

        // Handle tab routes
        const tab = pathToTab(path);
        if (tab !== 'feed') {
            dispatch(setActiveTab(tab));
        }

        // Handle deep links: /artist/name, /track/artist/title, /mix/artist/title, /mix/artist?filter=, /search?q=
        const artistMatch = path.match(/^\/artist\/([^/]+)$/);
        const trackMatch = path.match(/^\/track\/([^/]+)\/([^/]+)$/);
        const mixMatch = path.match(/^\/mix\/([^/]+)\/([^/]+)$/);
        const mixFilterMatch = path.match(/^\/mix\/([^/]+)$/);
        const searchMatch = path.match(/^\/search$/);
        const tracksMatch = path.match(/^\/tracks$/);

        if (searchMatch) {
            const q = new URLSearchParams(search).get('q') || '';
            if (q) {
                dispatch(setActiveTab('djpal'));
                setTimeout(() => {
                    dispatch(setActiveSearch('all'));
                    dispatch(setSearchAllQuery(q));
                }, 500);
            }
        } else if (tracksMatch) {
            const q = new URLSearchParams(search).get('q') || '';
            if (q) {
                dispatch(setActiveTab('djpal'));
                setTimeout(() => {
                    dispatch(setActiveSearch('trackList'));
                    dispatch(setTrackListQuery(q));
                    dispatch(searchTracksList({ query: q }));
                }, 500);
            }
        } else if (artistMatch) {
            dispatch(setActiveTab('djpal'));
            setTimeout(() => {
                const args = artistRouteToSearch(artistMatch, search);
                dispatch(setActiveSearch('artist'));
                dispatch(setArtistQuery(args.artistName));
                dispatch(searchArtist(args));
            }, 500);
        } else if (trackMatch) {
            dispatch(setActiveTab('djpal'));
            setTimeout(() => {
                const args = trackRouteToSearch(trackMatch, search);
                dispatch(setActiveSearch('track'));
                dispatch(setTrackQuery(args.trackName));
                dispatch(searchTrack(args));
            }, 500);
        } else if (mixMatch) {
            dispatch(setActiveTab('djpal'));
            setTimeout(() => {
                const { query, args } = mixRouteToSearch(mixMatch, search);
                dispatch(setActiveSearch('mix'));
                dispatch(setMixQuery(query));
                dispatch(searchMixes(args));
            }, 500);
        } else if (mixFilterMatch) {
            dispatch(setActiveTab('djpal'));
            setTimeout(() => {
                const artistName = fromSlug(mixFilterMatch[1]);
                const sp = new URLSearchParams(search);
                const filter = sp.get('filter');
                const eventPage = eventPageFromUrl(filter, artistName, sp.get('id'));
                if (eventPage) {
                    dispatch(setActiveSearch('mix'));
                    dispatch(setMixQuery(artistName));
                    dispatch(setMixFilterContext(eventPage.context));
                    dispatch(searchMixes(eventPage.args));
                    return;
                }
                const kind = filter === 'by' || filter === 'featuring' ? filter : 'by';
                const { filters, artistPublicId } = mixFilterFiltersFromUrl(kind, artistName, sp.get('id'));
                dispatch(setActiveSearch('mix'));
                dispatch(setMixQuery(artistName));
                dispatch(setMixFilterContext({ kind, artistName, artistPublicId }));
                dispatch(searchMixes({ filters }));
            }, 500);
        }
    }, [isAuthenticated, dispatch, pathToTab, isMobileWeb, MOBILE_ONLY_PATHS]);

    // Navigate to a deep-link path delivered by the OS at runtime — desktop
    // (Electron open-url → preload onDeepLink) and native (capacitor-shim
    // appUrlOpen → window.__mixgoatApplyDeepLink). Mirrors the initial-load URL
    // parser above, but works regardless of isWeb since the OS-delivered path
    // never touches window.location (no URL bar on desktop / native).
    const applyDeepLinkPath = useCallback((rawPath) => {
        if (!rawPath) return;
        const [path, query = ''] = String(rawPath).split('?');
        const sp = new URLSearchParams(query);

        const artistMatch = path.match(/^\/artist\/([^/]+)$/);
        const trackMatch = path.match(/^\/track\/([^/]+)\/([^/]+)$/);
        const mixMatch = path.match(/^\/mix\/([^/]+)\/([^/]+)$/);
        const mixFilterMatch = path.match(/^\/mix\/([^/]+)$/);
        if (!(artistMatch || trackMatch || mixMatch || mixFilterMatch)) return;

        dispatch(setActiveTab('djpal'));
        setTimeout(() => {
            if (artistMatch) {
                const args = artistRouteToSearch(artistMatch, query);
                dispatch(setActiveSearch('artist'));
                dispatch(setArtistQuery(args.artistName));
                dispatch(searchArtist(args));
            } else if (trackMatch) {
                const args = trackRouteToSearch(trackMatch, query);
                dispatch(setActiveSearch('track'));
                dispatch(setTrackQuery(args.trackName));
                dispatch(searchTrack(args));
            } else if (mixMatch) {
                const { query: mixQuery, args } = mixRouteToSearch(mixMatch, query);
                dispatch(setActiveSearch('mix'));
                dispatch(setMixQuery(mixQuery));
                dispatch(searchMixes(args));
            } else if (mixFilterMatch) {
                const artistName = fromSlug(mixFilterMatch[1]);
                const filter = sp.get('filter');
                const eventPage = eventPageFromUrl(filter, artistName, sp.get('id'));
                if (eventPage) {
                    dispatch(setActiveSearch('mix'));
                    dispatch(setMixQuery(artistName));
                    dispatch(setMixFilterContext(eventPage.context));
                    dispatch(searchMixes(eventPage.args));
                    return;
                }
                const kind = filter === 'by' || filter === 'featuring' ? filter : 'by';
                const { filters, artistPublicId } = mixFilterFiltersFromUrl(kind, artistName, sp.get('id'));
                dispatch(setActiveSearch('mix'));
                dispatch(setMixQuery(artistName));
                dispatch(setMixFilterContext({ kind, artistName, artistPublicId }));
                dispatch(searchMixes({ filters }));
            }
        }, 450);
    }, [dispatch]);

    // Wire OS deep-link delivery to applyDeepLinkPath. Buffers until authed.
    useEffect(() => {
        const handle = (rawPath) => {
            const path = typeof rawPath === 'string' ? rawPath : rawPath?.path;
            if (!path) return;
            if (!isAuthenticated) { pendingDeepLinkRef.current = path; return; }
            applyDeepLinkPath(path);
        };
        // Native (Capacitor) shim calls this global on appUrlOpen.
        window.__mixgoatApplyDeepLink = handle;
        // Desktop (Electron) main process pushes via the preload bridge.
        const unsub = window.electron?.onDeepLink ? window.electron.onDeepLink(handle) : null;
        return () => {
            if (typeof unsub === 'function') unsub();
            if (window.__mixgoatApplyDeepLink === handle) delete window.__mixgoatApplyDeepLink;
        };
    }, [isAuthenticated, applyDeepLinkPath]);

    // Flush a deep link that arrived pre-auth, once the user is signed in.
    useEffect(() => {
        if (isAuthenticated && pendingDeepLinkRef.current) {
            const path = pendingDeepLinkRef.current;
            pendingDeepLinkRef.current = null;
            applyDeepLinkPath(path);
        }
    }, [isAuthenticated, applyDeepLinkPath]);

    // Update URL when activeTab changes
    useEffect(() => {
        if (!isWeb) return;
        const newPath = tabToPath(activeTab);
        // Defensive: never put a mobile-only path into the desktop URL.
        if (!isMobileWeb && MOBILE_ONLY_PATHS.has(newPath)) return;
        if (window.location.pathname !== newPath) {
            window.history.pushState({ tab: activeTab }, '', newPath);
        }
    }, [activeTab, tabToPath, isMobileWeb, MOBILE_ONLY_PATHS]);

    // Handle browser back/forward
    useEffect(() => {
        if (!isWeb) return;
        const handlePopState = (e) => {
            const path = window.location.pathname;
            // Same desktop redirect as the initial deep-link handler, in
            // case the back/forward stack ever contains a mobile-only URL.
            if (!isMobileWeb && MOBILE_ONLY_PATHS.has(path)) {
                window.history.replaceState({ tab: 'feed' }, '', '/');
                dispatch(setActiveTab('feed'));
                if (path === '/likes') dispatch(setLikesSidebarOpen(true));
                else if (path === '/favourite-artists') dispatch(setFollowedArtistsSidebarOpen(true));
                else if (path === '/playlists') dispatch(setPlaylistSidebarOpen(true));
                return;
            }
            const tab = e.state?.tab || pathToTab(path);
            dispatch(setActiveTab(tab));

            // If we have a cached snapshot for this URL, restore it and skip
            // re-fetching. Cache is populated as state evolves during the session.
            const cacheKey = path + window.location.search;
            const cached = urlSnapshotCacheRef.current.get(cacheKey);
            if (cached) {
                dispatch(restoreDjpalSnapshot(cached));
                return;
            }

            // Cache miss (deep link / new session) — re-trigger the search.
            const artistMatch = path.match(/^\/artist\/([^/]+)$/);
            const trackMatch = path.match(/^\/track\/([^/]+)\/([^/]+)$/);
            const mixMatch = path.match(/^\/mix\/([^/]+)\/([^/]+)$/);
            const mixFilterMatch = path.match(/^\/mix\/([^/]+)$/);
            const searchMatch = path.match(/^\/search$/);
            const tracksMatch = path.match(/^\/tracks$/);

            if (searchMatch) {
                const q = new URLSearchParams(window.location.search).get('q') || '';
                if (q) {
                    setTimeout(() => { dispatch(setActiveSearch('all')); dispatch(setSearchAllQuery(q)); }, 100);
                }
            } else if (tracksMatch) {
                const q = new URLSearchParams(window.location.search).get('q') || '';
                if (q) {
                    setTimeout(() => {
                        dispatch(setActiveSearch('trackList'));
                        dispatch(setTrackListQuery(q));
                        dispatch(searchTracksList({ query: q }));
                    }, 100);
                }
            } else if (artistMatch) {
                const args = artistRouteToSearch(artistMatch, window.location.search);
                setTimeout(() => { dispatch(setActiveSearch('artist')); dispatch(setArtistQuery(args.artistName)); dispatch(searchArtist(args)); }, 100);
            } else if (trackMatch) {
                const args = trackRouteToSearch(trackMatch, window.location.search);
                setTimeout(() => { dispatch(setActiveSearch('track')); dispatch(setTrackQuery(args.trackName)); dispatch(searchTrack(args)); }, 100);
            } else if (mixMatch) {
                const { query: mixQuery, args } = mixRouteToSearch(mixMatch, window.location.search);
                setTimeout(() => { dispatch(setActiveSearch('mix')); dispatch(setMixQuery(mixQuery)); dispatch(searchMixes(args)); }, 100);
            } else if (mixFilterMatch) {
                const artistName = fromSlug(mixFilterMatch[1]);
                const sp = new URLSearchParams(window.location.search);
                const filter = sp.get('filter');
                const eventPage = eventPageFromUrl(filter, artistName, sp.get('id'));
                const kind = filter === 'by' || filter === 'featuring' ? filter : 'by';
                const { filters, artistPublicId } = mixFilterFiltersFromUrl(kind, artistName, sp.get('id'));
                setTimeout(() => {
                    dispatch(setActiveSearch('mix'));
                    dispatch(setMixQuery(artistName));
                    dispatch(setMixFilterContext(eventPage ? eventPage.context : { kind, artistName, artistPublicId }));
                    dispatch(searchMixes(eventPage ? eventPage.args : { filters }));
                }, 100);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [dispatch, pathToTab, isMobileWeb, MOBILE_ONLY_PATHS]);

    // Subscribe to store changes and cache the djpal snapshot under the current
    // URL. Only caches when the URL's shape matches the current activeSearch —
    // this skips transition states (drill-down click fires state changes before
    // pushState runs, and those snapshots would otherwise overwrite the outgoing
    // URL's cache with mid-navigation state).
    useEffect(() => {
        if (!isWeb) return;
        const urlMatchesState = (djpal) => {
            const path = window.location.pathname;
            const search = window.location.search;
            if (path === '/search') return djpal.activeSearch === 'all';
            if (path === '/tracks') return djpal.activeSearch === 'trackList';
            if (path.match(/^\/artist\/[^/]+$/)) return djpal.activeSearch === 'artist';
            if (path.match(/^\/track\/[^/]+\/[^/]+$/)) return djpal.activeSearch === 'track';
            if (path.match(/^\/mix\/[^/]+\/[^/]+$/)) return djpal.activeSearch === 'mix' && !djpal.mixFilterContext;
            if (path.match(/^\/mix\/[^/]+$/)) {
                const filter = new URLSearchParams(search).get('filter');
                return djpal.activeSearch === 'mix' && djpal.mixFilterContext?.kind === (filter === 'source' ? 'title' : filter);
            }
            return false;
        };
        const unsubscribe = store.subscribe(() => {
            const state = store.getState();
            if (state.app.activeTab !== 'djpal') return;
            const djpal = state.djpal;
            if (!urlMatchesState(djpal)) return;
            const snap = {};
            for (const key of DJPAL_SNAPSHOT_KEYS) snap[key] = djpal[key];
            const cacheKey = window.location.pathname + window.location.search;
            urlSnapshotCacheRef.current.set(cacheKey, snap);
        });
        return unsubscribe;
    }, [store]);

    // Update URL when Mixpedia search results change
    const artistQuery = useSelector((state) => state.djpal.artistQuery);
    const trackQuery = useSelector((state) => state.djpal.trackQuery);
    const mixQuery = useSelector((state) => state.djpal.mixQuery);
    const searchAllQuery = useSelector((state) => state.djpal.searchAllQuery);
    const trackListQuery = useSelector((state) => state.djpal.trackListQuery);
    const activeSearch = useSelector((state) => state.djpal.activeSearch);
    const mixFilterContext = useSelector((state) => state.djpal.mixFilterContext);
    const mixResults = useSelector((state) => state.djpal.mixResults);
    const openedMixPublicId = useSelector((state) => state.djpal.mixPagePublicId);
    const artistResults = useSelector((state) => state.djpal.artistResults);
    const trackResults = useSelector((state) => state.djpal.trackResults);
    const openedArtistPublicId = useSelector((state) => state.djpal.artistPagePublicId);
    const openedTrackPublicId = useSelector((state) => state.djpal.trackPagePublicId);

    const toSlug = useCallback((s) => encodeURIComponent(s.trim().replace(/\s+/g, '-').toLowerCase()), []);

    // A single-result mix search IS the mix detail page (see MixSearch's
    // handleOpenMixPage). Carry that mix's public id in the URL so a refresh /
    // shared link re-opens THAT mix by id — the `/mix/<dj>/<title>` slugs are
    // display text and can't be searched back to one row.
    //
    // The id the page was OPENED with wins over the one derived from results:
    // it's known before the request goes out, so a mix page that failed to load
    // (offline) still gets an id-bearing URL — which is exactly the refresh the
    // user does once the connection is back.
    const mixPagePublicId = useMemo(() => {
        if (activeSearch !== 'mix' || mixFilterContext) return null;
        if (openedMixPublicId) return openedMixPublicId;
        return Array.isArray(mixResults) && mixResults.length === 1
            ? publicEntityId(mixResults[0])
            : null;
    }, [activeSearch, mixFilterContext, mixResults, openedMixPublicId]);

    // Same treatment for the artist and track pages. Both thunks PREFER an id
    // when the caller has one ("no normalized-name miss, no dupe-row
    // shadowing" — searchTrack even ships a `track_page_seed_mismatch`
    // tripwire), but a URL restore had only the slug to offer, so every
    // refresh / deep link / back-nav re-ran the text search those comments
    // warn about. Carry the id so the restore can use the good path too.
    const artistPagePublicId = useMemo(() => {
        if (activeSearch !== 'artist') return null;
        return openedArtistPublicId || publicEntityId(artistResults?.artist) || null;
    }, [activeSearch, artistResults, openedArtistPublicId]);

    const trackPagePublicId = useMemo(() => {
        if (activeSearch !== 'track') return null;
        return openedTrackPublicId || publicEntityId(trackResults?.trackInfo) || null;
    }, [activeSearch, trackResults, openedTrackPublicId]);

    useEffect(() => {
        if (!isWeb || activeTab !== 'djpal') return;
        let newPath = '/mixpedia';
        let newSearch = '';
        if (activeSearch === 'all' && searchAllQuery) {
            newPath = '/search';
            newSearch = `?q=${encodeURIComponent(searchAllQuery)}`;
        } else if (activeSearch === 'trackList' && trackListQuery) {
            newPath = '/tracks';
            newSearch = `?q=${encodeURIComponent(trackListQuery)}`;
        } else if (activeSearch === 'artist' && artistQuery) {
            newPath = `/artist/${toSlug(artistQuery)}`;
            if (artistPagePublicId) newSearch = `?id=${encodeURIComponent(artistPagePublicId)}`;
        } else if (activeSearch === 'track' && trackQuery) {
            const parts = trackQuery.split(' - ');
            newPath = parts.length >= 2
                ? `/track/${toSlug(parts[0])}/${toSlug(parts.slice(1).join(' - '))}`
                : `/track/${toSlug(trackQuery)}`;
            if (trackPagePublicId) newSearch = `?id=${encodeURIComponent(trackPagePublicId)}`;
        } else if (activeSearch === 'mix' && mixQuery) {
            // When viewing a filtered mix list (Mixes by X / Featuring X), use
            // the artist name + `?filter=by|featuring` so URL round-trips cleanly.
            if (mixFilterContext?.kind === 'title' && mixFilterContext.eventKind) {
                // an event / venue / source page: the id (a source) or the title hub
                newPath = `/mix/${toSlug(mixFilterContext.titleQuery || mixQuery)}`;
                newSearch = mixFilterContext.sourceId ? `?filter=source&id=${encodeURIComponent(mixFilterContext.sourceId)}` : '?filter=title';
            } else if (mixFilterContext?.artistName) {
                newPath = `/mix/${toSlug(mixFilterContext.artistName)}`;
                newSearch = `?filter=${mixFilterContext.kind}`;
                // The artist's public id rides along so a restore (reload,
                // back/forward, deep link) filters by id, not by a name that
                // can resolve to a namesake.
                if (mixFilterContext.artistPublicId) newSearch += `&id=${encodeURIComponent(mixFilterContext.artistPublicId)}`;
            } else {
                const parts = mixQuery.split(' - ');
                newPath = parts.length >= 2
                    ? `/mix/${toSlug(parts[0])}/${toSlug(parts.slice(1).join(' - '))}`
                    : `/mix/${toSlug(mixQuery)}`;
                if (mixPagePublicId) newSearch = `?id=${encodeURIComponent(mixPagePublicId)}`;
            }
        }
        const full = newPath + newSearch;
        if (window.location.pathname + window.location.search !== full) {
            // The id lands one render AFTER the results do, so the first push
            // is the id-less URL and this is its follow-up. Replace rather than
            // push, or every mix page would leave a dead id-less twin in the
            // back stack that Back lands on before the previous page.
            const isIdBackfill = (mixPagePublicId || artistPagePublicId || trackPagePublicId)
                && window.location.pathname === newPath
                && !new URLSearchParams(window.location.search).get('id');
            if (isIdBackfill) window.history.replaceState({ tab: 'djpal', search: activeSearch }, '', full);
            else window.history.pushState({ tab: 'djpal', search: activeSearch }, '', full);
        }
    }, [activeTab, activeSearch, artistQuery, trackQuery, mixQuery, searchAllQuery, trackListQuery, mixFilterContext, mixPagePublicId, artistPagePublicId, trackPagePublicId, toSlug]);

    useEffect(() => {
        const base = 'MixGoat';
        let page = null;

        if (activeTab === 'djpal') {
            if (activeSearch === 'artist' && artistQuery) page = artistQuery;
            else if (activeSearch === 'track' && trackQuery) page = trackQuery;
            else if (activeSearch === 'mix' && mixQuery) {
                if (mixFilterContext?.artistName) {
                    const kind = mixFilterContext.kind === 'featuring' ? 'Mixes featuring' : 'Mixes by';
                    page = `${kind} ${mixFilterContext.artistName}`;
                } else {
                    page = mixQuery;
                }
            }
            else if (activeSearch === 'all' && searchAllQuery) page = `Search · ${searchAllQuery}`;
            else if (activeSearch === 'trackList' && trackListQuery) page = `Tracks · ${trackListQuery}`;
            else page = 'Mixpedia';
        } else if (activeTab) {
            const feature = ALL_CATEGORIES
                .flatMap(c => c.features)
                .find(f => f.key === activeTab);
            if (feature) page = feature.title;
            else if (activeTab === 'about') page = 'About';
            else if (activeTab === 'subscription') page = 'Subscription';
            else if (activeTab === 'account') page = 'Account';
            else if (activeTab === 'settings') page = 'Settings';
            else if (activeTab === 'notifications') page = 'Notifications';
            else if (activeTab === 'backup') page = 'Cloud Backup';
        } else if (activeCategory) {
            const cat = ALL_CATEGORIES.find(c => c.id === activeCategory);
            if (cat) page = cat.label;
        }

        document.title = page ? `${page} · ${base}` : base;
    }, [activeTab, activeCategory, activeSearch, artistQuery, trackQuery, mixQuery, searchAllQuery, trackListQuery, mixFilterContext]);

    useEffect(() => {
        if (isAuthenticated && !wasAuthenticated) {
            setWasAuthenticated(true);
            showLoadingOverlay();
            // Let spinner establish itself, then start phased mount
            setTimeout(() => setMountPhase(1), 150);
        }
        if (!isAuthenticated && wasAuthenticated) {
            // Logged out — unmount app, hide overlay so login modal is visible
            setWasAuthenticated(false);
            setMountPhase(0);
            hideLoadingOverlay();
        }
    }, [isAuthenticated, wasAuthenticated]);

    // Advance phases with gaps for the spinner to breathe
    useEffect(() => {
        if (mountPhase >= 1 && mountPhase <= 3) {
            const timer = setTimeout(() => setMountPhase(p => p + 1), 80);
            return () => clearTimeout(timer);
        }
        if (mountPhase === 4) {
            // All phases done — hide overlay
            setTimeout(() => {
                hideLoadingOverlay();
                dispatch(setIsLoading(false));
                window.electron.analytics.track('app_open');
            }, 100);
        }
    }, [mountPhase, dispatch]);

    useEffect(() => {
        dispatch(loadSettings());
    }, [dispatch]);

    // Help → Report a Problem… in the application menu (Electron only)
    useEffect(() => {
        const unsubscribe = window.electron?.report?.onOpen?.(() => {
            dispatch(openReportModal({ source: 'help-menu' }));
        });
        return unsubscribe;
    }, [dispatch]);

    useEffect(() => {
        // loudnessSettings now loads via loadTabSettings (PERSISTED_SLICES).
        dispatch(loadCuePointsSettings());
        dispatch(loadTabSettings());
        // after loadTabSettings: the engine's stems support depends on the saved folder
        if (!isWeb && !isNative) dispatch(loadAutomixEngine());
    }, [dispatch]);

    useEffect(() => {
        // Load theme from localStorage instantly (no IPC round-trip).
        // New users (no entry yet) default to 'dark' — the brand UI is
        // designed dark-first and the login page on app.mixgoat.com
        // shouldn't flip to light just because the OS is set that way.
        const savedTheme = localStorage.getItem('mixgoat-theme') || 'dark';
        applyTheme(savedTheme);
        const v2 = readDesignV2();
        applyDesignV2(v2);
        dispatch(updateSettings({ designV2: v2 }));
        if (savedTheme !== theme) {
            dispatch(updateSettings({ theme: savedTheme }));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!theme) return undefined;
        applyTheme(theme);
        // When the user picked 'auto', track OS-level appearance changes so
        // dark↔light flips at sunset / system toggle without a page reload.
        if (theme !== 'auto') return undefined;
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => {
            applyTheme('auto');
        };
        // addEventListener is the modern API; addListener is the Safari < 14 fallback.
        if (mql.addEventListener) mql.addEventListener('change', onChange);
        else if (mql.addListener) mql.addListener(onChange);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', onChange);
            else if (mql.removeListener) mql.removeListener(onChange);
        };
    }, [theme]);

    // Sync the iOS WebView's overrideUserInterfaceStyle with the resolved
    // theme so the system keyboard popup matches the app appearance.
    // WKWebView doesn't honor CSS `color-scheme` for the keyboard — we
    // bridge to the native MixGoatTheme plugin instead. On web/Electron,
    // window.electron.theme is undefined and the optional-chain call is
    // a complete no-op (zero regression). For 'auto', we forward "system"
    // and let UIKit's .unspecified follow the OS appearance natively; we
    // ALSO refresh on prefers-color-scheme changes so a mid-session OS
    // dark↔light flip propagates to the native side too (since
    // .unspecified inherits from the system trait, this is mostly a
    // belt-and-suspenders — but cheap insurance).
    useEffect(() => {
        if (!theme) return undefined;
        const setNativeStyle = () => {
            try {
                if (typeof window === 'undefined') return;
                const setStyle = window.electron && window.electron.theme && window.electron.theme.setStyle;
                if (typeof setStyle !== 'function') return;
                const nativeStyle = theme === 'auto' ? 'system' : resolveTheme(theme);
                setStyle(nativeStyle);
            } catch (_) { /* fire-and-forget */ }
        };
        setNativeStyle();
        if (theme !== 'auto') return undefined;
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => setNativeStyle();
        if (mql.addEventListener) mql.addEventListener('change', onChange);
        else if (mql.addListener) mql.addListener(onChange);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', onChange);
            else if (mql.removeListener) mql.removeListener(onChange);
        };
    }, [theme]);

    // After auth resolves, hydrate the theme preference from the server so
    // it follows the user across devices. Server value wins over localStorage.
    useEffect(() => {
        if (!isAuthenticated) return;
        dispatch(loadPreferences());
    }, [isAuthenticated, dispatch]);

    useEffect(() => {
        // Signal to Electron that React app is fully loaded and ready
        const timer = setTimeout(() => {
            if (window.electron && window.electron.appReady) {
                window.electron.appReady();
            }
            // Capacitor native: hide the splash screen now that React is
            // mounted and rendered. capacitor.config sets launchAutoHide:
            // false so splash stays up through startup work — preventing
            // the user from tapping into an unresponsive UI. This timeout
            // (1s) is enough for FeedTab + 150 cards to finish their
            // initial render in practice. Capped to ensure splash NEVER
            // sticks forever if something goes wrong.
            if (isNative && typeof window !== 'undefined' && window.Capacitor) {
                try {
                    const SplashScreenPlugin = window.Capacitor.Plugins.SplashScreen;
                    if (SplashScreenPlugin && typeof SplashScreenPlugin.hide === 'function') {
                        SplashScreenPlugin.hide({ fadeOutDuration: 200 }).catch(() => {});
                    }
                } catch (_) { /* fire-and-forget */ }
            }
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    // Safety net: even if the 1s timer above is skipped for any reason
    // (component unmount race, etc.), hide the splash after a hard
    // ceiling so it can never hang the app behind a perpetual splash.
    useEffect(() => {
        if (!isNative || typeof window === 'undefined' || !window.Capacitor) return;
        const safety = setTimeout(() => {
            try {
                const SplashScreenPlugin = window.Capacitor.Plugins.SplashScreen;
                if (SplashScreenPlugin && typeof SplashScreenPlugin.hide === 'function') {
                    SplashScreenPlugin.hide().catch(() => {});
                }
            } catch (_) {}
        }, 5000);
        return () => clearTimeout(safety);
    }, []);

    // Apply dynamic margin to tab content and music player based on sidebar state.
    // Layout effect, not useEffect: during a drag-resize the sidebar width and
    // this margin must land in the SAME paint, or the content trails a frame
    // behind and the gap flashes the app background.
    useLayoutEffect(() => {
        const styleId = 'sidebar-margin-styles';
        let styleElement = document.getElementById(styleId);

        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }

        // DESIGN V2 (desktop): the 232px sidebar replaces the 52px rail (its
        // collapsed 64px state overrides this margin in design-v2/_chrome.scss).
        const rail = designV2 && !isWeb ? 232 : 52;
        let marginLeft = `${rail}px`;
        // DESIGN V2: collection / DJ panels open as pages over the content column.
        if (designV2 && !isWeb) { /* no push */ } else if (sidebarOpen) {
            marginLeft = `${rail + sidebarWidth}px`;
        } else if (rekordboxSidebarOpen) {
            marginLeft = `${rail + rekordboxSidebarWidth}px`;
        } else if (seratoSidebarOpen) {
            marginLeft = `${rail + seratoSidebarWidth}px`;
        } else if (traktorSidebarOpen) {
            marginLeft = `${rail + traktorSidebarWidth}px`;
        } else if (virtualdjSidebarOpen) {
            marginLeft = `${rail + virtualdjSidebarWidth}px`;
        } else if (likesSidebarOpen) {
            marginLeft = `${rail + likesSidebarWidth}px`;
        } else if (followedArtistsSidebarOpen) {
            marginLeft = `${rail + followedArtistsSidebarWidth}px`;
        }

        styleElement.textContent = `
            .tab-content {
                margin-left: ${marginLeft} !important;
                transition: margin-left 0.2s ease;
            }
        `;
    }, [sidebarOpen, sidebarWidth, rekordboxSidebarOpen, rekordboxSidebarWidth, seratoSidebarOpen, seratoSidebarWidth, traktorSidebarOpen, traktorSidebarWidth, virtualdjSidebarOpen, virtualdjSidebarWidth, likesSidebarOpen, likesSidebarWidth, followedArtistsSidebarOpen, followedArtistsSidebarWidth, designV2]);

    return (
        <ToastProvider>
            <ConfirmationToastBridge />
            <DesignLab />
            <DjLibraryChangeListener />
            <AutoSyncManager />
            <DjSyncStateHydrator />
            {!isNative && !isWeb && <RestoreFoundPrompt />}
            {!isNative && !isWeb && <BackupOfferPrompt />}
            {!isNative && !isWeb && <ShrinkGuardPrompt />}
            {!isNative && !isWeb && <BackupProgressToast />}
            {!isNative && !isWeb && <AutomixBuildWatcher />}
            <LoginModal />
            <RegisterModal />
            <ForgotPasswordModal />
            <ReportProblemModal />
            {isNative ? <MobilePaywall /> : <GoatUpgradeModal />}
            <OnboardingFlow appReady={mountPhase >= 4} />
            <ForceUpgradeScreen />
            {!isNative && !isWeb && <UpdateNudgeBanner />}
            {mountPhase === 0 ? null : (
            <div className={`App app-container${isWeb ? ' is-web' : ''}${isMobileWeb ? ' is-mobile-web' : ''}${(sidebarOpen || likesSidebarOpen || followedArtistsSidebarOpen || rekordboxSidebarOpen || seratoSidebarOpen || traktorSidebarOpen || virtualdjSidebarOpen) ? ' has-panel-open' : ''}${mountPhase >= 3 && playerMode === 'full' ? ' player-full-mode' : ''}`}>
                {/* Phase 1: sidebars (lightweight) */}
                <PlaylistSidebar />
                {!isWeb && <RekordboxSidebar />}
                {!isWeb && <SeratoSidebar />}
                {!isWeb && <TraktorSidebar />}
                {!isWeb && <VirtualDJSidebar />}
                <LikedTracksSidebar />
                <FollowedArtistsSidebar />
                <div className="main-content">
                    <TrialBanner />
                    {/* Mobile nav header (web only, visible on small screens via CSS) */}
                    {isWeb && mountPhase >= 2 && (
                    <div className="mobile-nav-header">
                        <span style={{ cursor: 'pointer' }} onClick={() => dispatch(setActiveTab('feed'))}>
                            <MixGoatLogo width={28} height={28} />
                        </span>
                        <div className="mobile-nav-right">
                            <NotificationBell />
                            <UserMenu />
                        </div>
                    </div>
                    )}
                    {/* Phase 2: tabs + active tab content (heaviest - Recharts) */}
                    {mountPhase >= 2 && isWeb && (
                    <>
                        <div id="app-tabs" className="web-navbar">
                            <span className="web-navbar-logo" onClick={() => dispatch(setActiveTab('feed'))}>
                                <MixGoatLogo width={46} height={46} />
                            </span>
                            <nav className="web-navbar-tabs">
                                <button
                                    className={`web-navbar-tab ${(activeTab || 'feed') === 'feed' ? 'active' : ''}`}
                                    onClick={() => dispatch(setActiveTab('feed'))}
                                >
                                    For You
                                </button>
                                <button
                                    className={`web-navbar-tab ${activeTab === 'whats-next' ? 'active' : ''}`}
                                    onClick={() => dispatch(setActiveTab('whats-next'))}
                                >
                                    Set Planner
                                </button>
                                <button
                                    className={`web-navbar-tab ${activeTab === 'smart-recs' ? 'active' : ''}`}
                                    onClick={() => dispatch(setActiveTab('smart-recs'))}
                                >
                                    Crate Digger
                                </button>
                            </nav>
                            <div className="web-navbar-center">
                                <NavbarSearch />
                            </div>
                            <div className="nav-right-container">
                                {!hasDesktopApp && (
                                <a className="web-navbar-install-btn" href="https://mixgoat.com/download" target="_blank" rel="noopener noreferrer">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <polyline points="8 12 12 16 16 12" />
                                        <line x1="12" y1="8" x2="12" y2="16" />
                                    </svg>
                                    <span>Install app</span>
                                </a>
                                )}
                                <NotificationBell />
                                <UserMenu />
                            </div>
                        </div>
                        <div className="tab-content">
                            {CATEGORIES[0]?.features.map((feature) => (
                                (activeTab || 'feed') === feature.key && <feature.component key={feature.key} />
                            ))}
                            {activeTab === 'about' && <AboutTab />}
                            {activeTab === 'subscription' && <SubscriptionTab />}
                            {activeTab === 'account' && <AccountTab />}
                            {activeTab === 'settings' && <SettingsTab />}
                            {activeTab === 'notifications' && <NotificationsTab />}
                            {activeTab === 'backup' && <BackupTab />}
                            {isMobileWeb && activeTab === 'library' && <LibraryTab />}
                            {isMobileWeb && activeTab === 'likes' && <LikedTracksSidebar asPage />}
                            {isMobileWeb && activeTab === 'followed-artists' && <FollowedArtistsSidebar asPage />}
                            {isMobileWeb && activeTab === 'playlists' && <PlaylistSidebar asPage />}
                        </div>
                    </>
                    )}
                    {mountPhase >= 2 && !isWeb && (
                    <div className="desktop-tabs-wrap">
                    <Tabs
                        id="app-tabs"
                        activeKey={activeTab === 'about' || activeTab === 'subscription' || activeTab === 'notifications' || activeTab === 'account' || activeTab === 'settings' || activeTab === 'backup' ? activeTab : tabKey}
                        onSelect={handleSelectCategory}
                        mountOnEnter
                    >
                        <Tab className="logo" title={
                            <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); dispatch(setActiveCategory('explore')); }}>
                                <MixGoatLogo width={46} height={46} />
                            </span>
                        }></Tab>
                        {CATEGORIES.map((cat) => (
                            <Tab key={cat.id} eventKey={cat.id} title={cat.label}>
                                <CategoryView
                                    category={cat}
                                    features={cat.features}
                                />
                            </Tab>
                        ))}
                        <Tab eventKey="about" title="About" tabClassName="hidden-tab">
                            <AboutTab />
                        </Tab>
                        <Tab eventKey="subscription" title="Subscription" tabClassName="hidden-tab">
                            <SubscriptionTab />
                        </Tab>
                        <Tab eventKey="account" title="Account" tabClassName="hidden-tab">
                            <AccountTab />
                        </Tab>
                        <Tab eventKey="settings" title="Settings" tabClassName="hidden-tab">
                            <SettingsTab />
                        </Tab>
                        <Tab eventKey="notifications" title="Notifications" tabClassName="hidden-tab">
                            <NotificationsTab />
                        </Tab>
                        <Tab eventKey="backup" title="Cloud Backup" tabClassName="hidden-tab">
                            <BackupTab />
                        </Tab>
                        <Tab tabClassName="nav-right-items" title={
                            <div className="nav-right-container" onClick={(e) => e.stopPropagation()}>
                                {!designV2 && <NavButtons />}
                                <NotificationBell />
                                {/* Wrapper span = anchor for the one-time appearance hint */}
                                <span ref={appearanceHintRef} style={{ display: 'inline-flex' }}>
                                    <UserMenu />
                                </span>
                            </div>
                        }></Tab>
                    </Tabs>
                    {/* Same centered Mixpedia search as the web navbar
                        (.web-navbar-center). Absolutely centered over the tab
                        bar; the wrapper ignores clicks so the empty areas of
                        the navbar stay clickable. */}
                    {designV2 && <div className="v2-nav-history"><NavButtons /></div>}
                    <div className="desktop-navbar-search" ref={searchHintRef}>
                        <NavbarSearch />
                    </div>
                    </div>
                    )}
                    {searchHint && searchHintRef.current && (
                        <MixpediaHint
                            targetRef={searchHintRef}
                            title="Search everything"
                            message={`Search your own library plus Mixpedia's artists, tracks, and mixes from here. Looking for text on the current page instead? Press ${/^Mac/.test(navigator.platform) ? '⌘F' : 'Ctrl+F'}.`}
                            autoShow={searchHint.autoShow}
                            onDismiss={() => { markHintSeen('desktop_search'); setSearchHint(null); }}
                        />
                    )}
                    {mountPhase >= 2 && <NotificationPanel />}
                    {mountPhase >= 2 && !isWeb && <FindOverlay />}
                    {appearanceHint && appearanceHintRef.current && (
                        <MixpediaHint
                            targetRef={appearanceHintRef}
                            title="Make it yours"
                            message={`Too big or small on this screen? Adjust zoom (${/^Mac/.test(navigator.platform) ? '⌘' : 'Ctrl'} + / −) and switch light/dark theme from this menu — or fine-tune both in Settings → Appearance.`}
                            autoShow={appearanceHint.autoShow}
                            actionLabel="Open Settings"
                            onAction={() => {
                                markHintSeen('appearance_settings');
                                setAppearanceHint(null);
                                track('settings_opened', { from: 'appearance_hint' });
                                dispatch(setActiveTab('settings'));
                            }}
                            onDismiss={() => { markHintSeen('appearance_settings'); setAppearanceHint(null); }}
                        />
                    )}
                    {/* Phase 3: music player (audio lib init) */}
                    {mountPhase >= 3 && <MusicPlayer/>}
                    <ZoomIndicator/>
                    {mountPhase >= 3 && isNative && <NativeLockScreenRefill/>}
                    {mountPhase >= 3 && isNative && <NativeLockScreenLike/>}
                    {mountPhase >= 3 && isNative && <NativeSamplePrewarm/>}
                    {/*
                     * Same toast/CTA on native as web: mixes, set-planner,
                     * crate-digger are desktop-only on every non-Electron
                     * surface. The CTA messages already say "only available
                     * on the desktop app" which is correct for native too.
                     */}
                    {isWeb && mountPhase >= 3 && <WebPlaybackCTA />}
                    {isWeb && mountPhase >= 2 && <MobileBottomNav />}
                    {isWeb && mountPhase >= 3 && <NowPlayingPage />}
                </div>
            </div>
            )}
        </ToastProvider>
    );
}

export default App;
